export const layer4Template = `# Layer 4：Marveland 部署與 CI/CD 配置

> 目標：基於 Layer 1-3 的完成代碼，生成所有部署相關配置。
> **禁止**：修改任何應用程式代碼（.ts/.tsx/.css）。
> 輸入：Layer 1-3 已完成的可編譯專案。
> 輸出要求：可透過 \`npm run deploy\` 或 GitHub Actions 部署到 Cloudflare。

---

## 前置條件

Layer 1-3 已完成：
- ✅ 專案可編譯（\`npm run build\` 成功）
- ✅ Worker 路由完整
- ✅ 前台所有功能正常
- ✅ Git repository 已初始化

---

## 1. 環境變數設定

### apps/web/.env.example
\`\`\`
VITE_CMS_API_URL=https://marveland-cms.YOUR_SUBDOMAIN.workers.dev
\`\`\`

### apps/web/.env（本地開發，不進 git）
\`\`\`
VITE_CMS_API_URL=http://localhost:8787
\`\`\`

### apps/cms-worker/.dev.vars（本地開發，不進 git）
\`\`\`
ADMIN_PASSWORD=admin360
\`\`\`

### .gitignore（確認已包含）
\`\`\`
# Dependencies
node_modules/

# Build outputs
dist/

# Environment
.env
.env.local
.dev.vars

# IDE
.vscode/
.idea/

# OS
.DS_Store
Thumbs.db
\`\`\`

---

## 2. GitHub Actions CI/CD

### .github/workflows/deploy.yml
\`\`\`yaml
name: Deploy to Cloudflare

on:
  push:
    branches: [main]

jobs:
  deploy-worker:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      
      - name: Setup Node.js
        uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'
      
      - name: Install dependencies
        run: npm ci
      
      - name: Deploy Worker
        uses: cloudflare/wrangler-action@v3
        with:
          apiToken: \${{ secrets.CLOUDFLARE_API_TOKEN }}
          accountId: \${{ secrets.CLOUDFLARE_ACCOUNT_ID }}
          workingDirectory: apps/cms-worker
          command: deploy

  deploy-web:
    needs: deploy-worker
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      
      - name: Setup Node.js
        uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'
      
      - name: Install dependencies
        run: npm ci
      
      - name: Fetch CMS data for SSG
        run: node apps/web/scripts/fetch-cms.cjs
        env:
          CMS_API_URL: \${{ secrets.CMS_API_URL }}
      
      - name: Build frontend
        run: cd apps/web && npm run build
        env:
          VITE_CMS_API_URL: \${{ secrets.CMS_API_URL }}
      
      - name: Deploy to Cloudflare Pages
        uses: cloudflare/wrangler-action@v3
        with:
          apiToken: \${{ secrets.CLOUDFLARE_API_TOKEN }}
          accountId: \${{ secrets.CLOUDFLARE_ACCOUNT_ID }}
          command: pages deploy apps/web/dist --project-name=marveland
\`\`\`

### fetch-cms 腳本

\`apps/web/scripts/fetch-cms.cjs\`：
- 呼叫 Worker 的 \`/api/cms/data\` 取得最新 CMS 資料
- 寫入 \`apps/web/public/cms-data.json\`（SSG 時可用）
- 失敗時不中斷 build，使用內建預設值

\`\`\`js
const fs = require('fs');
const path = require('path');

async function fetchCMS() {
  const apiUrl = process.env.CMS_API_URL || 'http://localhost:8787';
  try {
    const res = await fetch(\`\${apiUrl}/api/cms/data\`);
    const data = await res.json();
    const outputPath = path.join(__dirname, '../public/cms-data.json');
    fs.writeFileSync(outputPath, JSON.stringify(data, null, 2));
    console.log('✅ CMS data fetched successfully');
  } catch (err) {
    console.warn('⚠️ Failed to fetch CMS data, using defaults:', err.message);
    process.exit(0); // 不中斷 build
  }
}

fetchCMS();
\`\`\`

---

## 3. 本地部署腳本

### scripts/deploy.ps1
\`\`\`powershell
#Requires -Version 5.1
[CmdletBinding()]
param(
    [switch]$Worker,
    [switch]$Web,
    [switch]$All
)

# 設定 UTF-8 輸出
$OutputEncoding = [System.Text.Encoding]::UTF8
[Console]::OutputEncoding = [System.Text.Encoding]::UTF8

$ErrorActionPreference = "Stop"

function Deploy-Worker {
    Write-Host "🚀 部署 Worker..." -ForegroundColor Cyan
    Push-Location apps/cms-worker
    try {
        npx wrangler deploy
        if ($LASTEXITCODE -ne 0) { throw "Worker deployment failed" }
        Write-Host "✅ Worker 部署完成" -ForegroundColor Green
    } finally {
        Pop-Location
    }
}

function Deploy-Web {
    Write-Host "🌐 構建並部署前端..." -ForegroundColor Cyan
    
    # 確認環境變數
    if (-not (Test-Path apps/web/.env)) {
        Write-Warning "apps/web/.env 不存在，請確認 VITE_CMS_API_URL 已設定"
    }
    
    Push-Location apps/web
    try {
        npm run build
        if ($LASTEXITCODE -ne 0) { throw "Build failed" }
        
        npx wrangler pages deploy dist --project-name=marveland
        if ($LASTEXITCODE -ne 0) { throw "Pages deployment failed" }
        
        Write-Host "✅ 前端部署完成" -ForegroundColor Green
    } finally {
        Pop-Location
    }
}

# 主邏輯
if ($All -or (!$Worker -and !$Web)) {
    Deploy-Worker
    Deploy-Web
} elseif ($Worker) {
    Deploy-Worker
} elseif ($Web) {
    Deploy-Web
}

Write-Host "\`n🎉 全部完成！" -ForegroundColor Green
\`\`\`

---

## 4. 生產環境初始化檢查清單

### Cloudflare 設定
- [ ] KV Namespace 已建立：\`wrangler kv:namespace create "CMS_DATA"\`
- [ ] KV Namespace ID 已填入 \`wrangler.toml\`
- [ ] 可選：R2 Bucket 已建立（大量圖片時）
- [ ] Pages 專案已建立：\`marveland\`

### Secrets 設定
\`\`\`bash
# 本地開發
cd apps/cms-worker
echo "admin360" > .dev.vars  # 或你的本地密碼

# 生產環境
wrangler secret put ADMIN_PASSWORD
# 輸入生產密碼
\`\`\`

### GitHub Secrets
| Secret | 值 | 來源 |
|---|---|---|
| \`CLOUDFLARE_API_TOKEN\` | 你的 API Token | Cloudflare Dashboard → My Profile → API Tokens |
| \`CLOUDFLARE_ACCOUNT_ID\` | 你的 Account ID | Cloudflare Dashboard 右下角 |
| \`CMS_API_URL\` | Worker URL | 首次 deploy Worker 後取得，如 \`https://marveland-cms.xxx.workers.dev\` |

### DNS / 自訂域名（可選）
- [ ] 在 Cloudflare Pages 設定自訂域名
- [ ] Worker 的 CORS origin 加入自訂域名

---

## 5. README.md

\`\`\`markdown
# Marveland 金晉物業代理 CMS

## 技術棧
- 前台：React 18 + TypeScript + Vite + Tailwind CSS
- 後端：Cloudflare Worker + Hono
- 儲存：Cloudflare KV
- 部署：Wrangler 3 + GitHub Actions

## 本地開發

\`\`\`bash
# 安裝依賴
npm install

# 設定環境變數
cp apps/web/.env.example apps/web/.env
# 編輯 apps/web/.env，填入本地 Worker URL

# 同時啟動前台與 Worker
npm run dev
\`\`\`

## 部署

### 手動部署
\`\`\`powershell
# 全部部署
.\\scripts\\deploy.ps1 -All

# 只部署 Worker
.\\scripts\\deploy.ps1 -Worker

# 只部署前端
.\\scripts\\deploy.ps1 -Web
\`\`\`

### GitHub Actions 自動部署
推送至 \`main\` 分支即可觸發自動部署。

## 環境變數

| 變數 | 用途 | 設定位置 |
|---|---|---|
| \`VITE_CMS_API_URL\` | 前台 API 基礎 URL | \`apps/web/.env\` |
| \`ADMIN_USERNAME\` | 後台帳號 | \`wrangler.toml\` |
| \`ADMIN_PASSWORD\` | 後台密碼 | \`wrangler secret put\` |

## 目錄結構

\`\`\`
apps/
├── web/        # Vite React 前台 + 後台
└── cms-worker/ # Cloudflare Worker API
\`\`\`
\`\`\`

---

## 6. AGENTS.md（給 AI 智能體的專案說明）

\`\`\`markdown
# Marveland Project Agent Guide

## 專案概述
金晉物業代理 CMS，部署於 Cloudflare Pages + Workers + KV。

## 技術約束
- 不可使用 Node.js 原生模組（Worker 環境）
- 不可在 Worker 中使用 \`fs\`、\`path\` 等模組
- KV 讀寫必須非同步（await）
- 圖片儲存於 KV base64，單筆上限 25MB
- 前端環境變數必須以 \`VITE_\` 前綴

## 修改規則
1. 修改 \`types/cms.ts\` 時必須同時修改 \`apps/cms-worker/src/types.ts\`
2. 新增 API 端點時必須更新前端的呼叫邏輯
3. 修改 brand 資料時必須同步 \`default.ts\`
4. 所有 commit 前執行 \`npm run build\` 確認無錯誤

## 常見指令
- \`npm run dev\`：本地開發
- \`npm run build\`：構建前台
- \`cd apps/cms-worker && npx wrangler dev\`：本地 Worker
- \`cd apps/cms-worker && npx wrangler deploy\`：部署 Worker
- \`cd apps/web && npx wrangler pages deploy dist\`：部署前台
\`\`\`

---

## 7. 生成完成後的驗證步驟

1. \`wrangler.toml\` 的 \`id\` 是否為實際 KV namespace ID（非佔位符）
2. \`.env.example\` 是否正確，\`.env\` 與 \`.dev.vars\` 是否已列入 \`.gitignore\`
3. GitHub Actions 檔案語法是否正確（可用 \`act\` 本地測試）
4. \`deploy.ps1\` 是否在 UTF-8 環境下正常執行
5. \`fetch-cms.cjs\` 是否能在 build 前正確執行
6. 生產環境 \`ADMIN_PASSWORD\` 是否已透過 \`wrangler secret put\` 設定
7. 首次部署 Worker 後，Worker URL 是否正確填入 GitHub Secret \`CMS_API_URL\`
8. Pages 專案名稱是否與 \`deploy.ps1\` 和 GitHub Actions 中的一致

---

## 8. 常見陷阱（Layer 4 特別關注）

1. **佔位符未替換**：\`YOUR_KV_NAMESPACE_ID\` 必須替換為實際 ID
2. **Secrets 未設定**：GitHub Secrets 必須手動設定，不會自動產生
3. **API Token 權限**：必須包含 Workers Scripts:Edit 和 Cloudflare Pages:Edit
4. **CORS origin**：生產環境必須加入 Pages 網域和自訂域名
5. **build 依賴環境變數**：\`VITE_CMS_API_URL\` 必須在 build 前設定
6. **KV 命名空間**：建立後要將返回的 ID 填入 \`wrangler.toml\`
7. **Pages 首次部署**：可能需要在 Dashboard 手動建立專案
`;
